import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { euro, todayISO, can } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useQuick } from "../components/Layout";
import { StatusBadge, PayBadge } from "../components/StatusBadge";
import { CalendarDays, Users, Stethoscope, CreditCard, Receipt, Wallet, Dumbbell, Cpu, Clock, Plus, LogIn, AlertTriangle } from "lucide-react";

const KpiCard = ({ icon: Icon, label, value, sub, color, tid }) => (
  <div className="kpi-card bg-white rounded-2xl border border-slate-100 p-5 fade-up" data-testid={tid}>
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-widest text-slate-400">{label}</p>
      <span className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center text-white`}><Icon size={17} strokeWidth={1.8} /></span>
    </div>
    <p className="font-heading text-3xl font-bold text-slate-900 mt-2">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { openModal } = useQuick();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const [period, setPeriod] = useState("oggi");
  const [rangeData, setRangeData] = useState(null);
  const isStaff = can(user, "cassa", "view");

  const load = useCallback(async () => {
    try {
      const [{ data: d }, { data: b }] = await Promise.all([
        api.get("/dashboard"),
        api.get("/machines/status/board"),
      ]);
      setData(d);
      setBoard(b);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("ll:data-changed", h);
    const t = setInterval(load, 60000);
    return () => { window.removeEventListener("ll:data-changed", h); clearInterval(t); };
  }, [load]);

  useEffect(() => {
    if (!isStaff || period === "oggi") { setRangeData(null); return; }
    const today = new Date();
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    let from = iso(today);
    if (period === "settimana") { const s = new Date(today); s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); from = iso(s); }
    if (period === "mese") from = iso(new Date(today.getFullYear(), today.getMonth(), 1));
    api.get("/billing/summary", { params: { date_from: from, date_to: iso(today) } }).then(({ data }) => setRangeData(data)).catch(() => {});
  }, [period, isStaff, data]);

  if (!data) return <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" /></div>;

  const k = data.kpi;
  const entrate = rangeData ? rangeData.entrate_totali : k.entrate;
  const spese = rangeData ? rangeData.spese : k.spese;
  const saldo = rangeData ? rangeData.saldo : k.saldo;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="dashboard-title">
            {user?.role === "fisioterapista" ? "La mia giornata · " : ""}{new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
          </h1>
          <p className="text-slate-500 text-sm mt-1">Ciao {user?.name?.split(" ")[0]}, ecco la situazione del centro.</p>
        </div>
        {isStaff && (
          <div className="flex bg-white rounded-full border border-slate-200 p-1" data-testid="period-selector">
            {["oggi", "settimana", "mese"].map((p) => (
              <button key={p} data-testid={`period-${p}`} onClick={() => setPeriod(p)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${period === p ? "bg-blue-600 text-white" : "text-slate-500"}`}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Azioni rapide */}
      <div className="flex flex-wrap gap-2" data-testid="quick-actions-bar">
        <button data-testid="qa-appointment" onClick={() => openModal("appointment")} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"><Plus size={16} /> Appuntamento</button>
        <button data-testid="qa-patient" onClick={() => openModal("patient")} className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2.5 rounded-full text-sm font-medium hover:border-blue-300 transition-colors"><Plus size={16} /> Paziente</button>
        <button data-testid="qa-gym" onClick={() => openModal("gym")} className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2.5 rounded-full text-sm font-medium hover:border-emerald-300 transition-colors"><Dumbbell size={16} /> Ingresso palestra</button>
        {can(user, "pagamenti", "create") && <>
          <button data-testid="qa-payment" onClick={() => openModal("payment")} className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2.5 rounded-full text-sm font-medium hover:border-violet-300 transition-colors"><CreditCard size={16} /> Pagamento</button>
          <button data-testid="qa-expense" onClick={() => openModal("expense")} className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2.5 rounded-full text-sm font-medium hover:border-amber-300 transition-colors"><Receipt size={16} /> Spesa</button>
        </>}
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="kpi-grid">
        <KpiCard tid="kpi-appointments" icon={CalendarDays} label={`Appuntamenti ${period}`} value={k.appuntamenti} sub={`${k.completati} completati · ${k.annullati} annullati`} color="bg-blue-600" />
        <KpiCard tid="kpi-present" icon={Users} label="Pazienti presenti" value={k.arrivati} sub={`${k.da_arrivare} ancora da arrivare`} color="bg-cyan-500" />
        <KpiCard tid="kpi-physios" icon={Stethoscope} label="Fisioterapisti presenti" value={data.physios_present.length} sub={data.physios_present.map((p) => p.name.split(" ")[0]).join(", ") || "Nessuno in turno ora"} color="bg-violet-500" />
        <KpiCard tid="kpi-gym" icon={Dumbbell} label="Ingressi palestra" value={k.gym_oggi} sub={`${k.gym_presenti} in palestra ora`} color="bg-emerald-500" />
        {isStaff && <>
          <KpiCard tid="kpi-revenue" icon={CreditCard} label={`Entrate ${period}`} value={euro(entrate)} color="bg-blue-500" />
          <KpiCard tid="kpi-expenses" icon={Receipt} label={`Spese ${period}`} value={euro(spese)} color="bg-amber-500" />
          <KpiCard tid="kpi-balance" icon={Wallet} label={`Saldo ${period}`} value={euro(saldo)} color={saldo >= 0 ? "bg-emerald-600" : "bg-red-500"} />
        </>}
        <KpiCard tid="kpi-machines" icon={Cpu} label="Macchinari" value={`${k.macchinari_disponibili} liberi`} sub={`${k.macchinari_occupati} in uso · ${k.macchinari_manutenzione} in manutenzione`} color="bg-slate-600" />
      </div>

      {k.in_ritardo > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3" data-testid="late-warning">
          <AlertTriangle className="text-amber-500" size={20} />
          <p className="text-sm text-amber-800"><strong>{k.in_ritardo} appuntament{k.in_ritardo > 1 ? "i" : "o"} in ritardo:</strong> {data.in_ritardo_list.map((a) => `${a.patient_name} (${a.start})`).join(", ")}</p>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 p-6" data-testid="timeline-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold">Timeline di oggi</h2>
            <button data-testid="goto-agenda" onClick={() => navigate("/agenda")} className="text-sm text-blue-600 hover:underline">Vai all'agenda</button>
          </div>
          {data.timeline.length === 0 ? (
            <p className="text-slate-400 text-sm py-8 text-center" data-testid="timeline-empty">Nessun appuntamento oggi. Clicca "+ Appuntamento" per iniziare.</p>
          ) : (
            <div className="space-y-1">
              {data.timeline.map((a) => (
                <button key={a.id} data-testid={`timeline-appt-${a.id}`} onClick={() => openModal("appointment", { appointment: a })}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors text-left group">
                  <span className="font-mono text-sm font-semibold text-slate-700 w-12">{a.start}</span>
                  <span className="w-1 self-stretch rounded-full" style={{ background: a.physio_color }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900 truncate">{a.patient_name}</p>
                    <p className="text-xs text-slate-400 truncate">{a.treatment_name || "Trattamento"} · {a.physio_name}{a.machine_name ? ` · ${a.machine_name}` : ""}</p>
                  </div>
                  {a.notes && <span className="hidden sm:block text-xs text-slate-400 italic max-w-32 truncate">{a.notes}</span>}
                  <StatusBadge status={a.status} />
                  <PayBadge status={a.payment_status} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Macchinari */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="machines-mini-board">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold">Macchinari ora</h2>
            <button data-testid="goto-machines" onClick={() => navigate("/macchinari")} className="text-sm text-blue-600 hover:underline">Gestisci</button>
          </div>
          <div className="space-y-4">
            {(board?.groups || []).map((g) => (
              <div key={g.type.id}>
                <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">{g.type.name} — {g.units.length} unità</p>
                <div className="space-y-1.5">
                  {g.units.map((u) => (
                    <div key={u.id} data-testid={`machine-status-${u.id}`} className="flex items-center gap-2 text-sm">
                      <span className={`w-2.5 h-2.5 rounded-full ${u.state === "disponibile" ? "bg-emerald-500" : u.state === "in_uso" ? "bg-red-500" : "bg-amber-500"}`} />
                      <span className="font-medium">{u.name}</span>
                      <span className="text-xs text-slate-400 ml-auto">
                        {u.state === "disponibile" && "Disponibile"}
                        {u.state === "in_uso" && `${u.state_detail?.patient} · fino ${u.state_detail?.until}`}
                        {u.state === "manutenzione" && "Manutenzione"}
                        {u.state === "disattivato" && "Disattivato"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {(!board || board.groups.length === 0) && <p className="text-sm text-slate-400">Nessun macchinario configurato.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
